import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Badge from '../../../components/Badge';
import { Recording } from '../../../queries/ats/recordings/types/recording';
import { Asset } from '../../../queries/ats/titles/types/asset';
import { ProgramSource } from '../../../queries/ats/titles/types/sources';
import { getPreferredRecording } from '../../../queries/ats/titles/utils/recording-utils';
import { getTimeStatus } from '../details-call-to-action/utils';

interface ProgramBadgeProps {
  asset: Asset;
  source: ProgramSource;
}

export function ProgramBadge({ asset, source }: ProgramBadgeProps) {
  const { t } = useTranslation();
  const status = getTimeStatus(source);
  const hideBadge = asset.type === 'series' || status === 'present' || status === 'future';
  const recording = getPreferredRecording(source, true);

  if (recording !== undefined) {
    return <RecordingBadge recording={recording} />;
  }

  if (!source.permissions.supportsCatchup) {
    return <Badge text={t('details:only-live')} type="dark" />;
  }

  return hideBadge ? null : <ExpireBadge expiresAt={source.expiresAt} />;
}

function ExpireBadge({ expiresAt }: { expiresAt: string }) {
  const { t } = useTranslation();

  const diff = moment(expiresAt).diff(moment());

  if (diff > 0) {
    return <Badge text={`${t('common:expires')} ${moment(expiresAt).fromNow()}`} type="dark" />;
  }

  return <Badge text={`${t('common:expired-since')} ${moment(expiresAt).fromNow()}`} type="dark" />;
}

interface RecordingBadgeProps {
  recording: Recording;
}

function RecordingBadge({ recording }: RecordingBadgeProps) {
  const { t } = useTranslation();

  if (recording.status === 'completed') {
    return <Badge text={t('details:recorded')} type="light" />;
  }

  if (recording.status === 'ongoing') {
    return <Badge text={t('details:recording')} type="primary" />;
  }

  return null;
}
